import React, { createContext, useCallback, useContext, useEffect, useRef, useState, ReactNode } from 'react'
import { useAuthContext } from './AuthContext'
import { notificationsService, NotificationRecipientItem } from '../services/notifications'
import {
  getPermission,
  requestPermission,
  showBrowserNotification,
  type BrowserNotificationPermission,
} from '../utils/browserNotifications'

interface NotificationsContextType {
  unreadCount: number
  items: NotificationRecipientItem[]
  loading: boolean
  refresh: () => Promise<void>
  markAsRead: (recipientId: string) => Promise<void>
  deleteOne: (recipientId: string) => Promise<void>
  clearAll: () => Promise<void>
  browserPermission: BrowserNotificationPermission
  requestBrowserPermission: () => Promise<BrowserNotificationPermission>
}

const BASE_DOCUMENT_TITLE = 'PASI QG'

function formatDocumentTitle(unreadCount: number): string {
  if (unreadCount <= 0) return BASE_DOCUMENT_TITLE
  const label = unreadCount > 99 ? '99+' : String(unreadCount)
  return `(${label}) ${BASE_DOCUMENT_TITLE}`
}

const NotificationsContext = createContext<NotificationsContextType | undefined>(undefined)

export const useNotificationsContext = () => {
  const context = useContext(NotificationsContext)
  if (context === undefined) {
    throw new Error('useNotificationsContext must be used within a NotificationsProvider')
  }
  return context
}

interface NotificationsProviderProps {
  children: ReactNode
}

export const NotificationsProvider: React.FC<NotificationsProviderProps> = ({ children }) => {
  const { user } = useAuthContext()
  const [unreadCount, setUnreadCount] = useState<number>(0)
  const [items, setItems] = useState<NotificationRecipientItem[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const [browserPermission, setBrowserPermission] = useState<BrowserNotificationPermission>(() => getPermission())

  const readyRef = useRef(false)
  const seenIdsRef = useRef<Set<string>>(new Set())
  const userIdRef = useRef<string | undefined>(undefined)
  userIdRef.current = user?.id

  const requestBrowserPermission = useCallback(async () => {
    const result = await requestPermission()
    setBrowserPermission(result)
    return result
  }, [])

  const applyBrowserToasts = useCallback((list: NotificationRecipientItem[]) => {
    if (!readyRef.current) return

    for (const it of list) {
      if (it.read_at) continue
      if (seenIdsRef.current.has(it.id)) continue

      showBrowserNotification({
        title: it.notification?.title || 'PASI QG',
        body: it.notification?.body || '',
        tag: it.id,
      })
    }

    for (const it of list) {
      seenIdsRef.current.add(it.id)
    }
  }, [])

  const load = useCallback(async (options?: { seed?: boolean }) => {
    const userId = userIdRef.current
    if (!userId) return

    const isSeed = options?.seed === true
    setLoading(true)
    try {
      const [{ count }, { data }] = await Promise.all([
        notificationsService.fetchUnreadCount(userId),
        notificationsService.fetchLatest(15, userId)
      ])
      const list = data || []

      if (isSeed) {
        seenIdsRef.current = new Set(list.map((it) => it.id))
        readyRef.current = true
      } else if (readyRef.current) {
        applyBrowserToasts(list)
      }

      setUnreadCount(count || 0)
      setItems(list)
    } finally {
      setLoading(false)
    }
  }, [applyBrowserToasts])

  const markAsRead = useCallback(async (recipientId: string) => {
    const { error } = await notificationsService.markAsRead(recipientId)
    if (!error) {
      setItems(prev => prev.map(it => it.id === recipientId ? { ...it, read_at: new Date().toISOString() } : it))
      setUnreadCount(prev => Math.max(0, prev - 1))
    }
  }, [])

  const deleteOne = useCallback(async (recipientId: string) => {
    const target = items.find(it => it.id === recipientId)
    const wasUnread = target && !target.read_at
    setItems(prev => prev.filter(it => it.id !== recipientId))
    if (wasUnread) setUnreadCount(prev => Math.max(0, prev - 1))

    const { error } = await notificationsService.deleteRecipient(recipientId)
    if (error) {
      await load()
    }
  }, [items, load])

  const clearAll = useCallback(async () => {
    if (!userIdRef.current) return
    setItems([])
    setUnreadCount(0)
    const { error } = await notificationsService.clearAllForUser(userIdRef.current)
    if (error) {
      await load()
    }
  }, [load])

  useEffect(() => {
    if (!user) {
      setUnreadCount(0)
      setItems([])
      readyRef.current = false
      seenIdsRef.current = new Set()
      return
    }

    let cancelled = false
    readyRef.current = false
    seenIdsRef.current = new Set()

    ;(async () => {
      await load({ seed: true })
      if (cancelled) return
      // Captura itens que chegaram durante o seed (realtime ignorado enquanto !ready)
      await load()
    })()

    const unsubscribe = notificationsService.subscribeRealtime(user.id, () => {
      if (!readyRef.current) return
      void load()
    })

    return () => {
      cancelled = true
      unsubscribe()
    }
  }, [user?.id, load])

  useEffect(() => {
    document.title = formatDocumentTitle(unreadCount)
  }, [unreadCount])

  useEffect(() => {
    const syncPermission = () => setBrowserPermission(getPermission())
    syncPermission()
    document.addEventListener('visibilitychange', syncPermission)
    return () => document.removeEventListener('visibilitychange', syncPermission)
  }, [])

  const value: NotificationsContextType = {
    unreadCount,
    items,
    loading,
    refresh: () => load(),
    markAsRead,
    deleteOne,
    clearAll,
    browserPermission,
    requestBrowserPermission,
  }

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  )
}
